import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useSeason } from "../context/SeasonContext";
import { Clock } from "lucide-react";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";
import StandingsSeasonSelector from "../components/StandingsSeasonSelector";
import JerseyNumber from "../utils/JerseyNumber";

const Schedule = () => {
  const { selectedSeason } = useSeason();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState("all");
  const [activeTab, setActiveTab] = useState("completed"); // 'upcoming' | 'completed'
  const [selectedGameForStats, setSelectedGameForStats] = useState(null);
  const [gameStats, setGameStats] = useState([]);

  const [selectedDate, setSelectedDate] = useState("all");

  const teams = selectedGameForStats
    ? [selectedGameForStats.team1, selectedGameForStats.team2]
    : [];
  const [activeTeamId, setActiveTeamId] = useState(teams[0]?.id || 0);

  // Load all regular season games
  const loadGames = useCallback(async () => {
    if (!selectedSeason) return;
    setLoading(true);

    const { data, error } = await supabase
      .from("games")
      .select(
        `
                *,
                team1:teams!games_team1_id_fkey(id, name, logo_url, archived_at, primary_color, secondary_color, gradient),
                team2:teams!games_team2_id_fkey(id, name, logo_url, archived_at, primary_color, secondary_color, gradient)
            `,
      )
      .eq("season_id", selectedSeason.id)
      .in("game_type", ["regular", "playoff"])
      .order("game_date", { ascending: true });

    if (!error) {
      const filtered = (data || []).filter(
        (g) => !g.team1?.archived_at && !g.team2?.archived_at,
      );
      setGames(filtered);
    }
    setLoading(false);
  }, [selectedSeason]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  useEffect(() => {
    if (selectedGameForStats) {
      // Fall back to team2 or index 0 if team1 is somehow missing
      const fallbackId =
        selectedGameForStats.team1?.id || selectedGameForStats.team2?.id || 0;
      setActiveTeamId(fallbackId);
    }
  }, [selectedGameForStats]);

  // Get unique teams for filter chips
  const allTeams = React.useMemo(() => {
    const teamsMap = new Map();
    games.forEach((game) => {
      if (game.team1) teamsMap.set(game.team1.id, game.team1);
      if (game.team2) teamsMap.set(game.team2.id, game.team2);
    });
    return Array.from(teamsMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [games]);

  // Filter games
  const filteredGames = games.filter((game) => {
    if (selectedTeamId === "all") return true;
    return (
      game.team1_id === parseInt(selectedTeamId) ||
      game.team2_id === parseInt(selectedTeamId)
    );
  });

  const now = new Date();
  const upcoming = filteredGames.filter((g) => new Date(g.game_date) >= now);
  const completed = filteredGames.filter((g) => g.status === "finished");

  const groupByDay = (gameList, descending = false) => {
    const grouped = gameList.reduce((acc, game) => {
      const dateKey = game.game_date.slice(0, 10);

      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(game);
      return acc;
    }, {});

    return Object.keys(grouped)
      .sort((a, b) => {
        const timeA = new Date(a).getTime();
        const timeB = new Date(b).getTime();
        return descending ? timeB - timeA : timeA - timeB;
      })
      .map((key) => ({
        dateKey: key,
        games: grouped[key].sort((a, b) => {
          const dateA = new Date(a.game_date).getTime();
          const dateB = new Date(b.game_date).getTime();
          return descending ? dateB - dateA : dateA - dateB;
        }),
      }));
  };

  const playoffRoundLabel = (round) => {
    switch (round) {
      case 1:
        return "Round 1";
      case 2:
        return "Quarterfinals";
      case 3:
        return "Semifinals";
      case 4:
        return "Finals";
      default:
        return "Playoff";
    }
  };

  const upcomingGrouped = groupByDay(upcoming, false);
  const completedGrouped = groupByDay(completed, true);

  const displayedCompleted =
    selectedDate === "all"
      ? completedGrouped
      : completedGrouped.filter((g) => g.dateKey === selectedDate);

  const loadGameStats = async (gameId) => {
    const { data } = await supabase
      .from("game_stats")
      .select(
        `
                *,
                player:players!game_stats_player_id_fkey(name, jersey_number, team_id)
            `,
      )
      .eq("game_id", gameId);

    setGameStats(data || []);
    setSelectedGameForStats(games.find((g) => g.id === gameId));
  };

  const nextGame = upcoming[0];

  if (loading) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        Loading schedule...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      {/* Season Selector */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-800">
        <StandingsSeasonSelector />
      </div>

      {/* Team Filter Chips */}
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Filter by Team
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedTeamId("all")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              selectedTeamId === "all"
                ? "bg-orange-600 text-white shadow"
                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
          >
            All Teams
          </button>

          {allTeams.map((team) => {
            const teamStyle = getTeamStyle(team);
            const isSelected = selectedTeamId === team.id.toString();
            // Compute a readable text color against the primary background
            const selectedTextColor = getTextColorForAccent(
              teamStyle.primaryHex,
              isDarkModeActive(),
            );
            return (
              <button
                key={team.id}
                onClick={() => setSelectedTeamId(team.id.toString())}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 shadow ${
                  !isSelected
                    ? "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    : ""
                }`}
                style={
                  isSelected
                    ? {
                        background: teamStyle.primaryHex,
                        color: selectedTextColor,
                      }
                    : undefined
                }
              >
                {team.logo_url && (
                  <img
                    src={team.logo_url}
                    alt={team.name}
                    className="w-5 h-5 rounded-full object-cover"
                  />
                )}
                {team.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Next Game Hero — Diagonal Split */}
      {nextGame &&
        (() => {
          const t1 = getTeamStyle(nextGame.team1);
          const t2 = getTeamStyle(nextGame.team2);
          const t1Primary = t1.primaryHex;
          const t1Secondary = t1.accentHex;
          const t2Primary = t2.primaryHex;
          const t2Secondary = t2.accentHex;
          const t1TextColor = getTextColorForAccent(
            t1Primary,
            isDarkModeActive(),
          );
          const t2TextColor = getTextColorForAccent(
            t2Primary,
            isDarkModeActive(),
          );
          const t1IsGradient = nextGame.team1?.gradient === true;
          const t2IsGradient = nextGame.team2?.gradient === true;
          const t1Bg = t1IsGradient
            ? `linear-gradient(160deg, ${t1Primary} 0%, ${t1Secondary} 100%)`
            : t1Primary;
          const t2Bg = t2IsGradient
            ? `linear-gradient(160deg, ${t2Primary} 0%, ${t2Secondary} 100%)`
            : t2Primary;

          return (
            <div
              className="rounded-3xl overflow-hidden relative"
              style={{
                minHeight: "60px",
                boxShadow: `
        0 0 0 2px ${t1Secondary}55,
        0 0 0 4px ${t2Secondary}44,
        0 20px 45px -14px rgba(0,0,0,0.55),
        0 0 50px -12px ${t1Secondary}30
      `,
              }}
            >
              {/* ============================================================
        BROADCAST BACKGROUND
    ============================================================ */}

              {/* Team 1 panel */}
              <div
                className="absolute inset-0"
                style={{
                  background: t1Bg,
                  clipPath: "polygon(0 0, 60% 0, 43% 100%, 0 100%)",
                }}
              />

              {/* Team 2 panel */}
              <div
                className="absolute inset-0"
                style={{
                  background: t2Bg,
                  clipPath: "polygon(60% 0, 100% 0, 100% 100%, 43% 100%)",
                }}
              />

              {/* Broadcast-style dark center wash */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(0,0,0,0.05) 0%, transparent 30%, rgba(0,0,0,0.18) 50%, transparent 70%, rgba(0,0,0,0.05) 100%)",
                }}
              />

              {/* Secondary color edge glow */}
              <div
                className="absolute inset-0 rounded-3xl pointer-events-none"
                style={{
                  boxShadow: `
          inset 0 0 0 1.5px ${t1Secondary}60,
          inset 0 0 18px 3px ${t1Secondary}18,
          inset 0 0 0 1.5px ${t2Secondary}60
        `,
                }}
              />

              {/* ============================================================
        CLEVELAND SKYLINE
        Subtle silhouette anchored to the bottom of the card.
        ============================================================ */}
              <div
                className="absolute bottom-0 left-0 right-0 pointer-events-none z-[1]"
                style={{
                  height: "76px",
                  opacity: 0.16,
                }}
              >
                <svg
                  viewBox="0 0 1200 150"
                  preserveAspectRatio="none"
                  className="w-full h-full"
                  aria-hidden="true"
                >
                  {/* Skyline silhouette */}
                  <path
                    d="
            M0 150
            L0 105
            L45 105
            L45 92
            L72 92
            L72 112
            L102 112
            L102 78
            L128 78
            L128 96
            L151 96
            L151 60
            L169 60
            L169 96
            L195 96
            L195 83
            L220 83
            L220 105
            L250 105
            L250 72
            L268 72
            L268 90
            L290 90
            L290 105
            L320 105
            L320 91
            L348 91
            L348 110
            L375 110

            L375 70
            L394 70
            L394 50
            L410 50
            L410 22
            L425 22
            L425 50
            L441 50
            L441 70
            L458 70
            L458 110

            L490 110
            L490 94
            L515 94
            L515 108
            L540 108
            L540 84
            L565 84
            L565 105

            L600 105
            L600 58
            L616 58
            L616 38
            L632 38
            L632 58
            L648 58
            L648 105

            L680 105
            L680 72
            L705 72
            L705 92
            L730 92
            L730 106

            L760 106
            L760 86
            L785 86
            L785 103
            L810 103
            L810 67
            L830 67
            L830 48
            L846 48
            L846 67
            L865 67
            L865 103

            L900 103
            L900 82
            L925 82
            L925 105
            L950 105
            L950 90
            L980 90
            L980 108
            L1010 108
            L1010 76
            L1035 76
            L1035 98
            L1060 98
            L1060 87
            L1090 87
            L1090 108
            L1120 108
            L1120 92
            L1150 92
            L1150 110
            L1200 110
            L1200 150
            Z
          "
                    fill="#ffffff"
                  />

                  {/* Terminal Tower-inspired spire */}
                  <path
                    d="
            M388 70
            L401 70
            L401 43
            L407 43
            L407 14
            L414 3
            L421 14
            L421 43
            L427 43
            L427 70
            L440 70
            Z
          "
                    fill="#ffffff"
                  />

                  {/* Cleveland shoreline / ground line */}
                  <rect x="0" y="108" width="1200" height="42" fill="#ffffff" />
                </svg>
              </div>

              {/* ============================================================
        CONTENT
        ============================================================ */}
              <div className="relative z-10 px-5 py-4 md:px-7 md:py-5">
                {/* ==========================================================
          BROADCAST HEADER
          ========================================================== */}
                <div className="flex items-center justify-between mb-3">
                  <div
                    className="flex items-center gap-2 text-[11px] md:text-xs font-black tracking-[0.22em] uppercase"
                    style={{
                      color: t1TextColor,
                      opacity: 0.9,
                    }}
                  >
                    <span
                      className="inline-flex items-center justify-center w-6 h-6 rounded-md"
                      style={{
                        background: "rgba(0,0,0,0.28)",
                        border: `1px solid ${t1TextColor}30`,
                      }}
                    >
                      <Clock size={13} />
                    </span>

                    <span>NEXT GAME</span>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Cleveland / league broadcast marker */}
                    <span
                      className="hidden sm:inline-block text-[9px] font-black tracking-[0.2em] uppercase px-2 py-1 rounded-sm"
                      style={{
                        color: "#ffffff",
                        background: "rgba(0,0,0,0.22)",
                        border: "1px solid rgba(255,255,255,0.16)",
                      }}
                    >
                      MUQALAB
                    </span>

                    {nextGame.game_type === "playoff" && (
                      <span
                        className="text-[10px] md:text-xs font-black tracking-wider uppercase px-2.5 py-1 rounded-sm"
                        style={{
                          color: "#111827",
                          background: "#facc15",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        }}
                      >
                        🏆 PLAYOFF
                      </span>
                    )}
                  </div>
                </div>

                {/* ==========================================================
          DATE STRIP
          ========================================================== */}
                <div className="flex justify-center mb-4">
                  <div
                    className="px-4 py-1 rounded-full text-sm md:text-base font-bold tracking-wide"
                    style={{
                      background: "rgba(0,0,0,0.28)",
                      color: "#ffffff",
                      backdropFilter: "blur(5px)",
                      WebkitBackdropFilter: "blur(5px)",
                      border: "1px solid rgba(255,255,255,0.12)",
                    }}
                  >
                    {new Date(nextGame.game_date).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>

                {/* ==========================================================
          MATCHUP
          ========================================================== */}
                <div className="flex items-center justify-between relative">
                  {/* --------------------------------------------------------
            TEAM 1
            -------------------------------------------------------- */}
                  <div className="flex-1 flex flex-col items-center text-center pr-5 md:pr-8">
                    {nextGame.team1?.logo_url && (
                      <div
                        className="w-14 h-14 md:w-16 md:h-16 rounded-full border-[3px] overflow-hidden shadow-lg mb-1.5"
                        style={{
                          borderColor: `${t1Primary}80`,
                          boxShadow: `0 0 13px 3px ${t1Primary}35`,
                        }}
                      >
                        <img
                          src={nextGame.team1.logo_url}
                          alt={nextGame.team1.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* Team name */}
                    <div
                      className="w-full flex flex-col items-center"
                      style={{ gap: "0px" }}
                    >
                      {(nextGame.team1?.name || "")
                        .toUpperCase()
                        .split(" ")
                        .map((word, i) => (
                          <svg
                            key={i}
                            width="100%"
                            height="36"
                            viewBox="0 0 400 36"
                            preserveAspectRatio="xMidYMid meet"
                            style={{
                              display: "block",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <defs>
                              <pattern
                                id={`kuf-t1-${i}`}
                                x="0"
                                y="0"
                                width="20"
                                height="20"
                                patternUnits="userSpaceOnUse"
                              >
                                {/* Base fabric */}
                                <rect
                                  width="20"
                                  height="20"
                                  fill={t1Secondary}
                                />

                                {/* =========================================================
      KEFFIYEH FISHNET / DIAMOND WEAVE
      ========================================================= */}

                                {/* Main diagonal weave */}
                                <path
                                  d="
      M-5 5 L5 -5
      M0 20 L20 0
      M15 25 L25 15

      M-5 15 L5 5
      M0 30 L30 0
      M15 35 L35 15
    "
                                  stroke="rgba(255,255,255,0.72)"
                                  strokeWidth="1.4"
                                  fill="none"
                                />

                                {/* Opposing diagonal weave */}
                                <path
                                  d="
      M-5 15 L5 25
      M-5 5 L15 25
      M5 -5 L25 15
      M15 -5 L25 5
    "
                                  stroke="rgba(0,0,0,0.38)"
                                  strokeWidth="1.2"
                                  fill="none"
                                />

                                {/* =========================================================
      TRADITIONAL-STYLE ROSETTE / CROSS MOTIF
      ========================================================= */}

                                <path
                                  d="
      M10 2
      L12 7
      L18 10
      L12 13
      L10 18
      L8 13
      L2 10
      L8 7
      Z
    "
                                  fill="none"
                                  stroke="rgba(255,255,255,0.78)"
                                  strokeWidth="1.15"
                                />

                                {/* Center dot */}
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="1.25"
                                  fill="rgba(255,255,255,0.9)"
                                />

                                {/* Small corner dots */}
                                <circle
                                  cx="2.5"
                                  cy="2.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.55)"
                                />
                                <circle
                                  cx="17.5"
                                  cy="2.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.55)"
                                />
                                <circle
                                  cx="2.5"
                                  cy="17.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.55)"
                                />
                                <circle
                                  cx="17.5"
                                  cy="17.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.55)"
                                />
                              </pattern>
                            </defs>

                            {/* Shadow */}
                            <text
                              x="200"
                              y="30"
                              textAnchor="middle"
                              fontFamily="'Arial Narrow','Impact','Haettenschweiler','Franklin Gothic Medium',sans-serif"
                              fontWeight="900"
                              fontSize="38"
                              letterSpacing="3"
                              fill="rgba(0,0,0,0.45)"
                              transform="translate(1.2,1.8)"
                            >
                              {word}
                            </text>

                            {/* Kufiya fill */}
                            <text
                              x="200"
                              y="30"
                              textAnchor="middle"
                              fontFamily="'Arial Narrow','Impact','Haettenschweiler','Franklin Gothic Medium',sans-serif"
                              fontWeight="900"
                              fontSize="38"
                              letterSpacing="3"
                              fill={`url(#kuf-t1-${i})`}
                              stroke="rgba(0,0,0,0.2)"
                              strokeWidth="0.5"
                            >
                              {word}
                            </text>

                            {/* Sheen */}
                            <text
                              x="200"
                              y="30"
                              textAnchor="middle"
                              fontFamily="'Arial Narrow','Impact','Haettenschweiler','Franklin Gothic Medium',sans-serif"
                              fontWeight="900"
                              fontSize="38"
                              letterSpacing="3"
                              fill="rgba(255,255,255,0.07)"
                            >
                              {word}
                            </text>
                          </svg>
                        ))}
                    </div>

                    <div
                      className="text-[9px] md:text-[10px] font-black tracking-[0.2em] mt-0.5 uppercase"
                      style={{
                        color: t1TextColor,
                        opacity: 0.62,
                      }}
                    >
                      HOME
                    </div>
                  </div>

                  {/* --------------------------------------------------------
            CENTER BROADCAST DIVIDER
            -------------------------------------------------------- */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center relative"
                    style={{
                      width: "52px",
                      alignSelf: "stretch",
                    }}
                  >
                    {/* Slash */}
                    <div
                      className="absolute"
                      style={{
                        width: "4px",
                        height: "82%",
                        background:
                          "linear-gradient(to bottom, transparent, rgba(255,255,255,0.9), transparent)",
                        transform: "skewX(-12deg)",
                        boxShadow: "0 0 12px rgba(0,0,0,0.35)",
                      }}
                    />

                    {/* VS badge */}
                    <div
                      className="relative z-10 px-2 py-1 rounded-md font-black italic text-[11px] md:text-xs tracking-wider"
                      style={{
                        background: "rgba(10,10,10,0.82)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.25)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                        transform: "skewX(-8deg)",
                      }}
                    >
                      VS
                    </div>
                  </div>

                  {/* --------------------------------------------------------
            TEAM 2
            -------------------------------------------------------- */}
                  <div className="flex-1 flex flex-col items-center text-center pl-5 md:pl-8">
                    {nextGame.team2?.logo_url && (
                      <div
                        className="w-14 h-14 md:w-16 md:h-16 rounded-full border-[3px] overflow-hidden shadow-lg mb-1.5"
                        style={{
                          borderColor: `${t2Primary}80`,
                          boxShadow: `0 0 13px 3px ${t2Primary}35`,
                        }}
                      >
                        <img
                          src={nextGame.team2.logo_url}
                          alt={nextGame.team2.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    {/* Team name */}
                    <div
                      className="w-full flex flex-col items-center"
                      style={{ gap: "0px" }}
                    >
                      {(nextGame.team2?.name || "")
                        .toUpperCase()
                        .split(" ")
                        .map((word, i) => (
                          <svg
                            key={i}
                            width="100%"
                            height="36"
                            viewBox="0 0 400 36"
                            preserveAspectRatio="xMidYMid meet"
                            style={{
                              display: "block",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <defs>
                              <pattern
                                id={`kuf-t2-${i}`}
                                x="0"
                                y="0"
                                width="20"
                                height="20"
                                patternUnits="userSpaceOnUse"
                              >
                                {/* Base fabric */}
                                <rect
                                  width="20"
                                  height="20"
                                  fill={t2Secondary}
                                />

                                {/* Main diagonal weave */}
                                <path
                                  d="
      M-5 5 L5 -5
      M0 20 L20 0
      M15 25 L25 15

      M-5 15 L5 5
      M0 30 L30 0
      M15 35 L35 15
    "
                                  stroke="rgba(255,255,255,0.68)"
                                  strokeWidth="1.4"
                                  fill="none"
                                />

                                {/* Opposing diagonal weave */}
                                <path
                                  d="
      M-5 15 L5 25
      M-5 5 L15 25
      M5 -5 L25 15
      M15 -5 L25 5
    "
                                  stroke="rgba(0,0,0,0.34)"
                                  strokeWidth="1.2"
                                  fill="none"
                                />

                                {/* Rosette / cross motif */}
                                <path
                                  d="
      M10 2
      L12 7
      L18 10
      L12 13
      L10 18
      L8 13
      L2 10
      L8 7
      Z
    "
                                  fill="none"
                                  stroke="rgba(255,255,255,0.72)"
                                  strokeWidth="1.15"
                                />

                                {/* Center dot */}
                                <circle
                                  cx="10"
                                  cy="10"
                                  r="1.25"
                                  fill="rgba(255,255,255,0.85)"
                                />

                                {/* Corner dots */}
                                <circle
                                  cx="2.5"
                                  cy="2.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.5)"
                                />
                                <circle
                                  cx="17.5"
                                  cy="2.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.5)"
                                />
                                <circle
                                  cx="2.5"
                                  cy="17.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.5)"
                                />
                                <circle
                                  cx="17.5"
                                  cy="17.5"
                                  r="0.8"
                                  fill="rgba(255,255,255,0.5)"
                                />
                              </pattern>
                            </defs>

                            {/* Shadow */}
                            <text
                              x="200"
                              y="33"
                              textAnchor="middle"
                              fontFamily="'Arial Narrow','Impact','Haettenschweiler','Franklin Gothic Medium',sans-serif"
                              fontWeight="900"
                              fontSize="38"
                              letterSpacing="3"
                              fill="rgba(0,0,0,0.45)"
                              transform="translate(1.2,1.8)"
                            >
                              {word}
                            </text>

                            {/* Kufiya fill */}
                            <text
                              x="200"
                              y="33"
                              textAnchor="middle"
                              fontFamily="'Arial Narrow','Impact','Haettenschweiler','Franklin Gothic Medium',sans-serif"
                              fontWeight="900"
                              fontSize="38"
                              letterSpacing="3"
                              fill={`url(#kuf-t2-${i})`}
                              stroke="rgba(0,0,0,0.2)"
                              strokeWidth="0.5"
                            >
                              {word}
                            </text>

                            {/* Sheen */}
                            <text
                              x="200"
                              y="33"
                              textAnchor="middle"
                              fontFamily="'Arial Narrow','Impact','Haettenschweiler','Franklin Gothic Medium',sans-serif"
                              fontWeight="900"
                              fontSize="38"
                              letterSpacing="3"
                              fill="rgba(255,255,255,0.07)"
                            >
                              {word}
                            </text>
                          </svg>
                        ))}
                    </div>

                    <div
                      className="text-[9px] md:text-[10px] font-black tracking-[0.2em] mt-0.5 uppercase"
                      style={{
                        color: t2TextColor,
                        opacity: 0.62,
                      }}
                    >
                      AWAY
                    </div>
                  </div>
                </div>

                {/* ==========================================================
          BROADCAST LOWER THIRD
          ========================================================== */}
                <div className="flex justify-center mt-3">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-xs md:text-sm font-bold"
                    style={{
                      background: "rgba(0,0,0,0.62)",
                      color: "#ffffff",
                      border: "1px solid rgba(255,255,255,0.16)",
                      backdropFilter: "blur(5px)",
                      WebkitBackdropFilter: "blur(5px)",
                      boxShadow: "0 3px 12px rgba(0,0,0,0.25)",
                    }}
                  >
                    <span className="font-black">
                      {new Date(nextGame.game_date).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "numeric",
                          minute: "2-digit",
                        },
                      )}
                    </span>

                    {nextGame.court && (
                      <>
                        <span
                          style={{
                            color: "rgba(255,255,255,0.35)",
                          }}
                        >
                          |
                        </span>

                        <span
                          className="tracking-wide"
                          style={{
                            color: "rgba(255,255,255,0.82)",
                          }}
                        >
                          {nextGame.court}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Broadcast accent line */}
                <div
                  className="mt-3 h-[2px] w-full rounded-full opacity-70"
                  style={{
                    background: `linear-gradient(
            90deg,
            ${t1Secondary},
            rgba(255,255,255,0.8) 50%,
            ${t2Secondary}
          )`,
                  }}
                />
              </div>
            </div>
          );
        })()}

      {/* Tabs */}
      <div className="flex border-b dark:border-gray-700">
        <button
          onClick={() => setActiveTab("upcoming")}
          className={`px-8 py-4 font-semibold border-b-2 transition-all ${
            activeTab === "upcoming"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Upcoming
        </button>
        <button
          onClick={() => setActiveTab("completed")}
          className={`px-8 py-4 font-semibold border-b-2 transition-all ${
            activeTab === "completed"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Completed Results
        </button>
      </div>

      {/* Upcoming Tab */}
      {activeTab === "upcoming" && (
        <div className="space-y-10">
          {upcomingGrouped.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              No upcoming games scheduled for the selected filter.
            </div>
          ) : (
            upcomingGrouped.map(({ dateKey, games: dayGames }) => (
              <div key={dateKey}>
                <h3 className="text-xl font-bold mb-5 text-gray-900 dark:text-white">
                  {new Date(dateKey.replace(/-/g, "/")).toLocaleDateString(
                    "en-US",
                    {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    },
                  )}
                </h3>
                <div className="space-y-4">
                  {dayGames.map((game) => {
                    const team1Style = getTeamStyle(game.team1);
                    const team2Style = getTeamStyle(game.team2);
                    const t1IsGradient = game.team1?.gradient === true;
                    const t2IsGradient = game.team2?.gradient === true;
                    const t1Bg = t1IsGradient
                      ? `linear-gradient(160deg, ${team1Style.primaryHex} 0%, ${team1Style.accentHex} 100%)`
                      : team1Style.primaryHex;
                    const t2Bg = t2IsGradient
                      ? `linear-gradient(160deg, ${team2Style.primaryHex} 0%, ${team2Style.accentHex} 100%)`
                      : team2Style.primaryHex;

                    return (
                      <div
                        key={game.id}
                        className="relative rounded-2xl overflow-hidden shadow-sm border dark:border-gray-700"
                      >
                        {/* Subtle split background tint */}
                        <div className="absolute inset-0 flex pointer-events-none">
                          <div
                            className="w-1/2 h-full opacity-[0.07] dark:opacity-[0.15]"
                            style={{ background: t1Bg }}
                          />
                          <div
                            className="w-1/2 h-full opacity-[0.07] dark:opacity-[0.15]"
                            style={{ background: t2Bg }}
                          />
                        </div>
                        {/* Dual accent bars */}
                        <div
                          className="absolute left-0 top-0 bottom-0 w-1.5"
                          style={{ background: t1Bg }}
                        />
                        <div
                          className="absolute right-0 top-0 bottom-0 w-1.5"
                          style={{ background: t2Bg }}
                        />

                        <div className="relative bg-white/85 dark:bg-gray-800/85 backdrop-blur-sm p-6 flex flex-col md:flex-row gap-6 items-center">
                          <div className="flex-1 flex items-center gap-2">
                            {/* Team 1 */}
                            <div className="flex-1 flex flex-col items-center text-center">
                              {game.team1?.logo_url && (
                                <div
                                  className="w-16 h-16 mb-3 rounded-full overflow-hidden border-2 shadow-md"
                                  style={{ borderColor: team1Style.primaryHex }}
                                >
                                  <img
                                    src={game.team1?.logo_url}
                                    alt={game.team1?.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="font-bold text-base text-gray-900 dark:text-gray-100">
                                {game.team1?.name}
                              </div>
                              <div
                                className="text-[10px] font-bold tracking-widest uppercase mt-0.5"
                                style={{
                                  color: team1Style.primaryHex,
                                  opacity: 0.75,
                                }}
                              >
                                HOME
                              </div>
                            </div>

                            {/* Diagonal slash divider */}
                            <div className="flex-shrink-0 flex items-center justify-center w-12">
                              <span
                                className="font-black select-none leading-none"
                                style={{
                                  fontSize: "2.25rem",
                                  background: `linear-gradient(135deg, ${team1Style.primaryHex} 0%, ${team2Style.primaryHex} 100%)`,
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                  transform: "skewX(-8deg)",
                                  display: "inline-block",
                                  filter:
                                    "drop-shadow(0 1px 2px rgba(0,0,0,0.12))",
                                }}
                              >
                                /
                              </span>
                            </div>

                            {/* Team 2 */}
                            <div className="flex-1 flex flex-col items-center text-center">
                              {game.team2?.logo_url && (
                                <div
                                  className="w-16 h-16 mb-3 rounded-full overflow-hidden border-2 shadow-md"
                                  style={{ borderColor: team2Style.primaryHex }}
                                >
                                  <img
                                    src={game.team2?.logo_url}
                                    alt={game.team2?.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="font-bold text-base text-gray-900 dark:text-gray-100">
                                {game.team2?.name}
                              </div>
                              <div
                                className="text-[10px] font-bold tracking-widest uppercase mt-0.5"
                                style={{
                                  color: team2Style.primaryHex,
                                  opacity: 0.75,
                                }}
                              >
                                AWAY
                              </div>
                            </div>
                          </div>

                          <div className="text-right text-sm text-gray-500 dark:text-gray-400 min-w-[100px]">
                            {game.game_type === "playoff" && (
                              <div className="mb-1">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400 mb-1">
                                  🏆 Playoff
                                </div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-purple-500 dark:text-purple-300">
                                  {playoffRoundLabel(game.playoff_round)}
                                </div>
                              </div>
                            )}
                            <div className="font-semibold">
                              {new Date(game.game_date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                },
                              )}
                            </div>
                            {game.court && (
                              <div className="mt-1">{game.court}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Completed Tab */}
      {activeTab === "completed" && (
        <div className="space-y-10">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-gray-500 uppercase">
              Date:
            </span>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-white dark:bg-gray-800 dark:text-gray-300 border dark:border-gray-700 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Games</option>
              {completedGrouped.map((g) => (
                <option key={g.dateKey} value={g.dateKey}>
                  {new Date(g.dateKey + "T00:00:00").toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                </option>
              ))}
            </select>
          </div>

          {displayedCompleted.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              No games found.
            </div>
          ) : (
            displayedCompleted.map(({ dateKey, games: dayGames }) => (
              <div key={dateKey}>
                <h3 className="text-xl font-bold mb-5 text-gray-900 dark:text-white">
                  {new Date(dateKey + "T00:00:00").toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </h3>
                <div className="space-y-4">
                  {dayGames.map((game) => {
                    const isTeam1Winner = game.team1_score > game.team2_score;
                    const isTeam2Winner = game.team2_score > game.team1_score;
                    const isTie = game.team1_score === game.team2_score;
                    const diff = Math.abs(game.team1_score - game.team2_score);
                    const winnerName = isTeam1Winner
                      ? game.team1?.name
                      : isTeam2Winner
                        ? game.team2?.name
                        : "Tie";

                    const team1Style = getTeamStyle(game.team1);
                    const team2Style = getTeamStyle(game.team2);

                    return (
                      <div
                        key={game.id}
                        onClick={() => loadGameStats(game.id)}
                        className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-2xl p-6 flex flex-col md:flex-row gap-6 items-center cursor-pointer hover:shadow-md transition"
                      >
                        <div className="flex-1 flex items-center gap-4 w-full">
                          {/* Team 1 */}
                          <div
                            className={`flex-1 flex flex-col items-center text-center transition-opacity ${isTeam2Winner ? "opacity-40" : "opacity-100"}`}
                          >
                            {game.team1?.logo_url && (
                              <img
                                src={game.team1?.logo_url}
                                alt={game.team1?.name}
                                className={`w-20 h-20 mb-4 rounded-full border-4 object-cover transition-all ${isTeam1Winner ? "shadow-lg scale-105" : "border-white/30"}`}
                                style={
                                  isTeam1Winner
                                    ? { borderColor: team1Style.accentHex }
                                    : undefined
                                }
                              />
                            )}
                            <div
                              className={`font-semibold text-lg ${isTeam1Winner ? "dark:text-white" : "text-gray-900 dark:text-gray-100"}`}
                              style={
                                isTeam1Winner
                                  ? { color: team1Style.primaryHex }
                                  : undefined
                              }
                            >
                              {game.team1?.name}
                            </div>
                            <div
                              className="text-3xl font-black"
                              style={
                                isTeam1Winner
                                  ? { color: team1Style.primaryHex }
                                  : { color: "#9ca3af" }
                              }
                            >
                              {game.team1_score}
                            </div>
                          </div>

                          <div className="text-4xl font-black text-gray-300 dark:text-gray-600">
                            –
                          </div>

                          {/* Team 2 */}
                          <div
                            className={`flex-1 flex flex-col items-center text-center transition-opacity ${isTeam1Winner ? "opacity-40" : "opacity-100"}`}
                          >
                            {game.team2?.logo_url && (
                              <img
                                src={game.team2?.logo_url}
                                alt={game.team2?.name}
                                className={`w-20 h-20 mb-4 rounded-full border-4 object-cover transition-all ${isTeam2Winner ? "shadow-lg scale-105" : "border-white/30"}`}
                                style={
                                  isTeam2Winner
                                    ? { borderColor: team2Style.accentHex }
                                    : undefined
                                }
                              />
                            )}
                            <div
                              className={`font-semibold text-lg ${isTeam2Winner ? "dark:text-white" : "text-gray-900 dark:text-gray-100"}`}
                              style={
                                isTeam2Winner
                                  ? { color: team2Style.primaryHex }
                                  : undefined
                              }
                            >
                              {game.team2?.name}
                            </div>
                            <div
                              className="text-3xl font-black"
                              style={
                                isTeam2Winner
                                  ? { color: team2Style.primaryHex }
                                  : { color: "#9ca3af" }
                              }
                            >
                              {game.team2_score}
                            </div>
                          </div>
                        </div>

                        <div className="text-center md:text-right min-w-[120px]">
                          {game.game_type === "playoff" && (
                            <div className="text-[10px] font-bold uppercase tracking-widest text-purple-600 dark:text-purple-400 mb-1">
                              🏆 Playoff
                            </div>
                          )}
                          <div className="text-xs uppercase tracking-widest text-green-600 font-bold mb-1">
                            {isTie ? "EVEN" : `+${diff} PTS`}
                          </div>
                          <div className="text-sm font-bold text-gray-700 dark:text-gray-300">
                            {isTie ? "It's a Tie" : `${winnerName}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Game Stats Modal */}
      {selectedGameForStats && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-all"
          onClick={() => setSelectedGameForStats(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-[2rem] max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border dark:border-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header: Scoreboard Style */}
            <div className="bg-gray-50 dark:bg-gray-800/50 p-8 border-b dark:border-gray-700">
              <div className="flex justify-between items-center mb-6">
                <span className="text-xs font-bold tracking-widest text-green-600 bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                  FINAL
                </span>
                <button
                  onClick={() => setSelectedGameForStats(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="flex items-center justify-between gap-4 max-w-2xl mx-auto">
                <div className="flex-1 flex flex-col items-center text-center">
                  <img
                    src={selectedGameForStats.team1?.logo_url}
                    className="w-16 h-16 mb-2 rounded-full border-2 border-white/20 shadow-sm"
                    alt=""
                  />
                  <div className="font-bold text-lg leading-tight dark:text-white">
                    {selectedGameForStats.team1?.name}
                  </div>
                  <div
                    className="text-4xl font-black"
                    style={{
                      color: getTeamStyle(selectedGameForStats.team1)
                        .primaryHex,
                    }}
                  >
                    {selectedGameForStats.team1_score}
                  </div>
                </div>

                <div className="text-2xl font-light text-gray-300">vs</div>

                <div className="flex-1 flex flex-col items-center text-center">
                  <img
                    src={selectedGameForStats.team2?.logo_url}
                    className="w-16 h-16 mb-2 rounded-full border-2 border-white/20 shadow-sm"
                    alt=""
                  />
                  <div className="font-bold text-lg leading-tight dark:text-white">
                    {selectedGameForStats.team2?.name}
                  </div>
                  <div
                    className="text-4xl font-black"
                    style={{
                      color: getTeamStyle(selectedGameForStats.team2)
                        .primaryHex,
                    }}
                  >
                    {selectedGameForStats.team2_score}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Table Section */}
            <div className="flex-1 min-h-0 p-2 sm:p-6 custom-scrollbar flex flex-col gap-4">
              {/* 2. Navigation Buttons (Tabs) */}
              <div className="flex gap-2 border-b dark:border-gray-800 pb-2">
                {teams.map((team, idx) => {
                  const teamId = team?.id || idx;
                  const isSelected = activeTeamId === teamId;
                  const teamStyle = getTeamStyle(team);
                  const accentHex = teamStyle.accentHex;
                  const isDark = isDarkModeActive();
                  const textColor = getTextColorForAccent(accentHex, isDark);

                  return (
                    <button
                      key={teamId}
                      onClick={() => setActiveTeamId(teamId)}
                      className={`px-4 py-2 rounded-lg font-bold text-sm sm:text-base tracking-tight transition-all duration-200 shadow-sm border
                                                ${
                                                  isSelected
                                                    ? "scale-[1.02] border-transparent"
                                                    : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                                                }`}
                      style={
                        isSelected
                          ? { background: accentHex, color: textColor }
                          : {}
                      }
                    >
                      {team?.name}
                    </button>
                  );
                })}
              </div>

              {/* 3. Render only the active team's box score */}
              {teams.map((team, idx) => {
                const teamId = team?.id || idx;

                const teamStyle = getTeamStyle(team);
                const accentHex = teamStyle.accentHex;

                // Skip rendering if this isn't the active tab
                if (activeTeamId !== teamId) return null;

                return (
                  <div key={teamId} className="w-full animate-fade-in">
                    <div className="max-h-[450px] overflow-y-auto overflow-x-auto rounded-xl border dark:border-gray-800 relative">
                      <table className="w-full text-sm border-separate border-spacing-0">
                        <thead className="text-gray-500 dark:text-gray-400">
                          <tr>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-left py-3 px-4 font-semibold uppercase tracking-wider">
                              Player
                            </th>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-center py-3 font-bold text-gray-900 dark:text-white">
                              PTS
                            </th>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-center py-3 font-semibold">
                              3PM
                            </th>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-center py-3 font-semibold">
                              FTM
                            </th>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-center py-3 font-semibold">
                              REB
                            </th>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-center py-3 font-semibold">
                              AST
                            </th>
                            <th className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 text-center py-3 font-semibold">
                              PF
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-gray-800">
                          {gameStats
                            .filter((s) => s.player?.team_id === team?.id)
                            .map((stat) => (
                              <tr
                                key={stat.id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                              >
                                <td className="py-4 px-4 whitespace-nowrap">
                                  <div className="font-bold text-gray-900 dark:text-gray-100">
                                    {stat.player?.name}
                                  </div>
                                  <JerseyNumber
                                    number={stat.player?.jersey_number}
                                    color={accentHex}
                                    size="xs"
                                    mode="inline"
                                  />
                                </td>
                                <td className="text-center text-lg font-black text-gray-900 dark:text-white">
                                  {stat.points}
                                </td>
                                <td className="text-center font-medium text-gray-600 dark:text-gray-400">
                                  {stat.three_pm}
                                </td>
                                <td className="text-center font-medium text-gray-600 dark:text-gray-400">
                                  {stat.ft_made}
                                </td>
                                <td className="text-center font-medium text-gray-600 dark:text-gray-400">
                                  {stat.rebounds}
                                </td>
                                <td className="text-center font-medium text-gray-600 dark:text-gray-400">
                                  {stat.assists}
                                </td>
                                <td
                                  className={`text-center font-medium ${stat.personal_fouls >= 5 ? "text-red-500" : "text-gray-400"}`}
                                >
                                  {stat.personal_fouls}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
